import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Field, Input, PasswordInput } from '../ui/Field';
import { pillPrimary } from '../ui/pills';
import { invitationApi, messageErreur } from '../../lib/equipeApi';

/**
 * Page publique du lien reçu par e-mail : création de l'identifiant (invitation) ou nouveau mot de passe
 * (réinitialisation). Non dessinée dans la maquette L02 : version minimale aux jetons existants, mobile d'abord,
 * consentement non précoché. Aucun mot de passe n'est affiché ni stocké en clair.
 */
type Infos = Awaited<ReturnType<typeof invitationApi.lire>>;

export const InvitationPublique: React.FC = () => {
    const { token = '' } = useParams();
    const [infos, setInfos] = useState<Infos | null>(null);
    const [invalide, setInvalide] = useState(false);
    const [identifiant, setIdentifiant] = useState('');
    const [mdp, setMdp] = useState('');
    const [mdp2, setMdp2] = useState('');
    const [consent, setConsent] = useState(false);
    const [erreur, setErreur] = useState('');
    const [envoi, setEnvoi] = useState(false);
    const [fait, setFait] = useState<string | null>(null);

    useEffect(() => {
        let vivant = true;
        invitationApi.lire(token).then((i) => { if (vivant) setInfos(i); }).catch(() => { if (vivant) setInvalide(true); });
        return () => { vivant = false; };
    }, [token]);

    const invitation = infos?.type === 'invitation';
    const erreurMdp = mdp && mdp.length < 10 ? 'Au moins 10 caractères.' : mdp2 && mdp !== mdp2 ? 'Les deux mots de passe ne correspondent pas.' : '';
    const pret = !!mdp && !erreurMdp && mdp === mdp2 && (!invitation || (identifiant.trim().length >= 2 && consent));

    const valider = async () => {
        setEnvoi(true); setErreur('');
        try { const r = await invitationApi.valider(token, { identifiant: invitation ? identifiant.trim() : undefined, motDePasse: mdp, consentement: invitation ? consent : undefined }); setFait(r.identifiant); }
        catch (e) { setErreur(messageErreur(e)); }
        finally { setEnvoi(false); }
    };

    return (
        <main className="min-h-screen bg-surface-app text-content flex items-start sm:items-center justify-center p-4">
            <div className="w-full max-w-[440px] bg-surface border border-border rounded-2xl p-6 space-y-5">
                <div className="text-[13px] font-bold tracking-tight text-content-muted">L'IAmani</div>
                {invalide && (
                    <div role="alert"><h1 className="text-[19px] font-bold">Ce lien n'est plus valable</h1><p className="text-[13px] text-content-muted mt-1">Il a expiré ou a déjà servi. Demandez une nouvelle invitation à la direction de votre établissement.</p></div>
                )}
                {!invalide && !infos && <p role="status" className="text-[13px] text-content-muted">Chargement…</p>}
                {infos && !fait && (
                    <>
                        <div><h1 className="text-[19px] font-bold tracking-tight">{invitation ? `Bonjour ${infos.nom.split(' ')[0]}` : 'Choisissez un nouveau mot de passe'}</h1>
                            <p className="text-[13px] text-content-muted mt-1">{invitation ? `${infos.entreprise} vous invite à créer votre accès personnel.` : `Compte de ${infos.nom} · ${infos.entreprise}.`}</p></div>
                        {invitation ? (
                            <Field label="Identifiant" required help={`Le début « ${infos.prefixe}. » indique votre établissement. Choisissez la fin : 2 à 30 caractères, lettres, chiffres, tiret. Personnel, jamais partagé.`}>{(f) => (
                                <div className="flex items-stretch">
                                    <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-border bg-surface-soft text-[13px] font-mono text-content-muted shrink-0 max-w-[55%] truncate" data-testid="prefixe-identifiant">{infos.prefixe}.</span>
                                    <Input {...f} className="!rounded-l-none" value={identifiant} onChange={(e) => setIdentifiant(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} autoComplete="username" autoCapitalize="none" maxLength={30} />
                                </div>
                            )}</Field>
                        ) : <Field label="Identifiant" disabled>{(f) => <Input {...f} value={infos.identifiant} disabled readOnly />}</Field>}
                        <Field label="Mot de passe" required help="Au moins 10 caractères.">{(f) => <PasswordInput {...f} value={mdp} onChange={(e) => setMdp(e.target.value)} autoComplete="new-password" />}</Field>
                        <Field label="Confirmer le mot de passe" required error={erreurMdp}>{(f) => <PasswordInput {...f} value={mdp2} onChange={(e) => setMdp2(e.target.value)} autoComplete="new-password" />}</Field>
                        {invitation && (
                            <label className="flex items-start gap-2 text-[12.5px] text-content-muted min-h-[44px]"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4" style={{ accentColor: 'var(--acc)' }} />
                                <span>J'ai lu la <Link to="/privacy" className="underline text-content">politique de confidentialité</Link> et j'accepte que mon identifiant et mes actions soient enregistrés dans le journal de mon établissement.</span></label>
                        )}
                        {erreur && <p role="alert" className="text-[13px] text-state-danger">{erreur}</p>}
                        <button type="button" className={`${pillPrimary(36)} w-full !h-11`} disabled={!pret || envoi} onClick={valider}>{invitation ? 'Créer mon accès' : 'Enregistrer mon mot de passe'}</button>
                    </>
                )}
                {fait && (
                    <div className="text-center space-y-2" role="status"><span className="mx-auto w-11 h-11 rounded-full bg-state-ok-soft text-state-ok flex items-center justify-center"><Check size={20} /></span>
                        <h1 className="text-[19px] font-bold">{invitation ? 'Votre accès est créé' : 'Mot de passe enregistré'}</h1>
                        <p className="text-[13px] text-content-muted">Votre identifiant : <b className="font-mono text-content">{fait}</b></p>
                        <Link to="/login" className={`${pillPrimary(36)} !inline-flex mt-2`}>Se connecter</Link></div>
                )}
            </div>
        </main>
    );
};

export default InvitationPublique;
